import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, MapPin, Plus, Edit, Car, DollarSign, TrendingUp, Users, Calendar,
  Building2, RefreshCw, Loader2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO_URL = "https://customer-assets.emergentagent.com/job_yolo-crop-counter/artifacts/dqftlupc_Recurso%2021VR%20logos.png";
const DISRUPTIVE_LOGO = "https://customer-assets.emergentagent.com/job_yolo-crop-counter/artifacts/qx3y7mcn_Recurso%202lgo%20disr.png";

const fmtMoney = (a) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(a || 0);

export default function SitesPage() {
  const { user } = useAuth();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', address: '', city: '', timezone: 'America/Argentina/Buenos_Aires' });
  const [saving, setSaving] = useState(false);

  const fetch = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/sites-stats`, { withCredentials: true });
      setSites(data);
    } catch {
      toast.error('Error cargando sedes');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ name: '', address: '', city: '', timezone: 'America/Argentina/Buenos_Aires' });
    setShowModal(true);
  };

  const openEdit = (site) => {
    setEditing(site);
    setForm({ name: site.name, address: site.address || '', city: site.city || '', timezone: 'America/Argentina/Buenos_Aires' });
    setShowModal(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Ingresa un nombre'); return; }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/admin/sites/${editing.id}`, { ...form, organization_id: user.organization_id }, { withCredentials: true });
        toast.success('Sede actualizada');
      } else {
        await axios.post(`${API}/admin/sites`, { ...form, organization_id: user.organization_id }, { withCredentials: true });
        toast.success('Sede creada');
      }
      setShowModal(false);
      fetch();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally { setSaving(false); }
  };

  const totals = sites.reduce((acc, s) => ({
    inside: acc.inside + s.vehicles_inside,
    today: acc.today + s.revenue_today,
    month: acc.month + s.revenue_month,
    entriesMonth: acc.entriesMonth + s.entries_month,
  }), { inside: 0, today: 0, month: 0, entriesMonth: 0 });

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      <header className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white" data-testid="sites-back-btn">
                  <ArrowLeft className="w-4 h-4 mr-2" />Volver
                </Button>
              </Link>
              <div className="border-l border-slate-700 pl-4">
                <h1 className="text-lg font-bold text-white flex items-center gap-2"><Building2 className="w-5 h-5" />Sedes / Estacionamientos</h1>
                <p className="text-xs sm:text-sm text-slate-400">Gestion y facturacion por sede</p>
              </div>
            </div>
            <img src={LOGO_URL} alt="ParkingVision AI" className="h-8 w-auto" />
          </div>
        </div>
      </header>

      <main className="max-w-7xl w-full mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-8 flex-1">
        {/* Totals row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <Card className="bg-slate-800 border-slate-700"><CardContent className="p-4"><div className="flex items-center gap-2 text-slate-400 text-xs"><Building2 className="w-4 h-4" />Sedes</div><p className="text-2xl font-bold text-white mt-1">{sites.length}</p></CardContent></Card>
          <Card className="bg-slate-800 border-slate-700"><CardContent className="p-4"><div className="flex items-center gap-2 text-slate-400 text-xs"><Car className="w-4 h-4" />Adentro ahora</div><p className="text-2xl font-bold text-emerald-400 mt-1">{totals.inside}</p></CardContent></Card>
          <Card className="bg-slate-800 border-slate-700"><CardContent className="p-4"><div className="flex items-center gap-2 text-slate-400 text-xs"><DollarSign className="w-4 h-4" />Hoy</div><p className="text-xl sm:text-2xl font-bold text-emerald-400 mt-1 break-words">{fmtMoney(totals.today)}</p></CardContent></Card>
          <Card className="bg-slate-800 border-slate-700"><CardContent className="p-4"><div className="flex items-center gap-2 text-slate-400 text-xs"><Calendar className="w-4 h-4" />Mes</div><p className="text-xl sm:text-2xl font-bold text-blue-400 mt-1 break-words">{fmtMoney(totals.month)}</p></CardContent></Card>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <h2 className="text-lg sm:text-xl font-bold text-white">Listado de sedes</h2>
          <div className="flex gap-2">
            <Button onClick={fetch} variant="outline" size="sm" className="border-slate-600 text-slate-200 bg-slate-700/40 hover:bg-slate-700" data-testid="sites-refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Refrescar
            </Button>
            <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700" data-testid="sites-new-btn">
              <Plus className="w-4 h-4 mr-2" />Nueva sede
            </Button>
          </div>
        </div>

        {loading && sites.length === 0 ? (
          <div className="text-center py-12 text-slate-400">Cargando...</div>
        ) : sites.length === 0 ? (
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="py-12 text-center">
              <Building2 className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400 mb-4">No hay sedes cargadas. Crea la primera para empezar a separar la facturacion por estacionamiento.</p>
              <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700"><Plus className="w-4 h-4 mr-2" />Crear primera sede</Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sites.map(s => (
              <Card key={s.id} className="bg-slate-800 border-slate-700 hover:border-slate-500 transition-colors" data-testid={`site-card-${s.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-white text-lg flex items-center gap-2"><MapPin className="w-4 h-4 text-amber-400 flex-shrink-0" /><span className="truncate">{s.name}</span></CardTitle>
                      <CardDescription className="text-slate-400 text-xs mt-1 truncate">{s.address || 'Sin direccion'} {s.city ? `- ${s.city}` : ''}</CardDescription>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(s)} className="text-slate-400 hover:text-white h-8 w-8 p-0 flex-shrink-0" data-testid={`site-edit-${s.id}`}>
                      <Edit className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3 pt-0">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-slate-700/40 rounded-lg p-2.5">
                      <div className="flex items-center gap-1 text-[10px] text-slate-500 uppercase tracking-wide"><Car className="w-3 h-3" />Adentro</div>
                      <p className="text-xl font-bold text-emerald-400">{s.vehicles_inside}</p>
                    </div>
                    <div className="bg-slate-700/40 rounded-lg p-2.5">
                      <div className="flex items-center gap-1 text-[10px] text-slate-500 uppercase tracking-wide"><Users className="w-3 h-3" />Usuarios</div>
                      <p className="text-xl font-bold text-blue-400">{s.users_count}</p>
                    </div>
                  </div>
                  <div className="bg-emerald-900/20 border border-emerald-800/40 rounded-lg p-2.5">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] text-emerald-300 uppercase tracking-wide font-semibold flex items-center gap-1"><TrendingUp className="w-3 h-3" />Hoy</span>
                      <span className="text-[10px] text-emerald-400/80">{s.entries_today} ingresos</span>
                    </div>
                    <p className="text-lg font-bold text-emerald-400 break-words">{fmtMoney(s.revenue_today)}</p>
                  </div>
                  <div className="bg-blue-900/20 border border-blue-800/40 rounded-lg p-2.5">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] text-blue-300 uppercase tracking-wide font-semibold flex items-center gap-1"><Calendar className="w-3 h-3" />Mes actual</span>
                      <span className="text-[10px] text-blue-400/80">{s.entries_month} ingresos</span>
                    </div>
                    <p className="text-lg font-bold text-blue-400 break-words">{fmtMoney(s.revenue_month)}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Card className="bg-amber-900/15 border-amber-700/40 mt-6">
          <CardContent className="p-4 text-amber-200 text-sm">
            <strong>Tip:</strong> los nuevos ingresos quedan asociados automaticamente a la sede del usuario que los registra (Terminal/Dashboard). Asignale a cada cajero su sede en <Link to="/users" className="underline hover:text-white">Usuarios</Link>.
          </CardContent>
        </Card>
      </main>

      <footer className="py-3 sm:py-4 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-3 flex flex-col sm:flex-row items-center justify-between gap-1">
          <img src={DISRUPTIVE_LOGO} alt="Disruptive" className="h-7 w-auto invert" />
          <span className="text-slate-500 text-xs">Desarrollado por <a href="https://www.disruptive-sw.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">Disruptive Software</a>. Todos los derechos reservados.</span>
          <span className="text-slate-500 text-xs ml-auto"><a href="mailto:info@disruptive-sw.com" className="hover:text-white">info@disruptive-sw.com</a></span>
        </div>
      </footer>

      {/* Site Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader><DialogTitle>{editing ? 'Editar sede' : 'Nueva sede'}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-slate-300">Nombre *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ej: Sede Centro" className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="site-name-input" autoFocus />
            </div>
            <div>
              <Label className="text-slate-300">Direccion</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="Av. Siempre Viva 123" className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="site-address-input" />
            </div>
            <div>
              <Label className="text-slate-300">Ciudad</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                placeholder="Buenos Aires" className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="site-city-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)} className="border-slate-600 text-slate-300" data-testid="site-cancel-btn">Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="site-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              {editing ? 'Guardar cambios' : 'Crear sede'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
